import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import api from "../lib/api";
import LawyerSilhouette from "../components/LawyerSilhouette";
import { ArrowLeft, Mail, Phone, Linkedin } from "lucide-react";
import { resolvePhotoUrl } from "../lib/photo";

export default function LawyerPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([
      api.get(`/team/${id}`).then((r) => setMember(r.data)).catch(() => setNotFound(true)),
      api.get(`/team`).then((r) => setTeam(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="pt-40 lg:pt-44 min-h-[60vh] flex items-center justify-center">
        <div className="font-serif-ap text-2xl text-[#0F2746]">...</div>
      </div>
    );
  }

  if (notFound || !member) {
    return (
      <div className="pt-40 lg:pt-44 min-h-[60vh] max-w-[1400px] mx-auto px-6 lg:px-12">
        <h1 className="font-serif-ap text-4xl text-[#0F2746]">404</h1>
        <p className="text-[#3A5170] mt-4">{lang === "tr" ? "Sayfa bulunamadı." : "Page not found."}</p>
        <Link to="/ekip" className="overline text-[#0F2746] mt-8 inline-block ap-link-underline">← {t.team.cta}</Link>
      </div>
    );
  }

  const variant = team.findIndex((m) => m.id === member.id);
  const others = team.filter((m) => m.id !== member.id).slice(0, 3);

  const expertiseStr = lang === "tr" ? member.expertise_tr : member.expertise_en;
  const expertiseList = expertiseStr
    ? expertiseStr.split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  return (
    <div data-testid="lawyer-page" className="pt-40 lg:pt-44">
      {/* Breadcrumb */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <button
          data-testid="lawyer-back-btn"
          onClick={() => navigate(-1)}
          className="overline text-[#3A5170] hover:text-[#0F2746] inline-flex items-center gap-2 transition-colors"
        >
          <ArrowLeft size={14} /> {t.team.cta}
        </button>
      </section>

      {/* Header: small portrait + name/title */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-10 pb-12 lg:pb-16">
        <div className="flex flex-col sm:flex-row gap-8 lg:gap-12 items-start">
          {/* Passport-format portrait */}
          <div className="w-[180px] lg:w-[220px] shrink-0">
            <div className="aspect-[4/5] w-full overflow-hidden border border-[#0F2746]/10">
              {member.photo_url ? (
                <img src={resolvePhotoUrl(member.photo_url)} alt={member.name} className="w-full h-full object-cover" />
              ) : (
                <LawyerSilhouette variant={variant >= 0 ? variant : 0} />
              )}
            </div>
          </div>

          {/* Name + title */}
          <div className="flex-1 pt-2">
            <div className="overline text-[#4A6A8F]">
              {lang === "tr" ? member.title_tr : member.title_en}
            </div>
            <h1
              data-testid="lawyer-name"
              className="font-serif-ap text-4xl sm:text-5xl lg:text-6xl text-[#0F2746] mt-3 leading-[1.05] tracking-tight"
            >
              {member.name}
            </h1>
            <div className="h-[1px] w-16 bg-[#C4A47C] my-6" />
            {/* Quick contact pills */}
            <div className="flex flex-wrap gap-3 text-sm">
              {member.email && (
                <a
                  href={`mailto:${member.email}`}
                  data-testid="lawyer-email-pill"
                  className="inline-flex items-center gap-2 text-[#0F2746] hover:text-[#C4A47C] transition-colors"
                >
                  <Mail size={14} /> {member.email}
                </a>
              )}
              {member.email && member.phone && <span className="text-[#0F2746]/30">·</span>}
              {member.phone && (
                <a
                  href={`tel:${member.phone.replace(/\s|\(|\)/g, "")}`}
                  data-testid="lawyer-phone-pill"
                  className="inline-flex items-center gap-2 text-[#0F2746] hover:text-[#C4A47C] transition-colors"
                >
                  <Phone size={14} /> {member.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Body: bio + expertise side by side */}
      <section className="bg-[#EEF1F5] py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Bio */}
          <div className="lg:col-span-7">
            <div className="overline text-[#4A6A8F]">
              {lang === "tr" ? "Özgeçmiş" : "Profile"}
            </div>
            <div className="h-[1px] w-12 bg-[#C4A47C] mt-3 mb-8" />
            {(lang === "tr" ? member.bio_tr : member.bio_en) ? (
              <p className="text-[#0F2746] text-lg leading-relaxed whitespace-pre-line">
                {lang === "tr" ? member.bio_tr : member.bio_en}
              </p>
            ) : (
              <p className="text-[#3A5170] italic">
                {lang === "tr" ? "Özgeçmiş yakında eklenecek." : "Profile coming soon."}
              </p>
            )}
          </div>

          {/* Expertise */}
          <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-[#0F2746]/10">
            <div className="overline text-[#4A6A8F]">
              {lang === "tr" ? "Uzmanlık Alanları" : "Areas of Expertise"}
            </div>
            <div className="h-[1px] w-12 bg-[#C4A47C] mt-3 mb-8" />
            {expertiseList.length > 0 ? (
              <ul className="space-y-3" data-testid="expertise-list">
                {expertiseList.map((e, i) => (
                  <li key={i} className="flex items-baseline gap-3 group">
                    <span className="font-serif-ap text-xs text-[#C4A47C]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-serif-ap text-xl lg:text-2xl text-[#0F2746] leading-snug">
                      {e}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[#3A5170] italic">
                {lang === "tr" ? "Uzmanlık alanları yakında eklenecek." : "Expertise areas coming soon."}
              </p>
            )}

            {/* LinkedIn block (if present) */}
            {member.linkedin && (
              <a
                href={/^https?:\/\//i.test(member.linkedin) ? member.linkedin : `https://${member.linkedin}`}
                target="_blank"
                rel="noreferrer"
                className="mt-10 inline-flex items-center gap-2 overline text-[#0F2746] hover:text-[#C4A47C] transition-colors"
              >
                <Linkedin size={14} /> LinkedIn →
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Other team members — small thumbnails */}
      {others.length > 0 && (
        <section className="bg-white py-16 lg:py-24">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="overline text-[#4A6A8F]">
              {lang === "tr" ? "Diğer ekip üyeleri" : "Other team members"}
            </div>
            <div className="h-[1px] w-12 bg-[#C4A47C] mt-3 mb-10" />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-10">
              {others.map((m) => {
                const idx = team.findIndex((x) => x.id === m.id);
                return (
                  <Link
                    key={m.id}
                    to={`/ekip/${m.id}`}
                    data-testid={`other-lawyer-${m.id}`}
                    className="group flex items-center gap-5"
                  >
                    {/* small passport thumbnail */}
                    <div className="w-[88px] h-[110px] shrink-0 overflow-hidden border border-[#0F2746]/10">
                      {m.photo_url ? (
                        <img
                          src={resolvePhotoUrl(m.photo_url)}
                          alt={m.name}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                        />
                      ) : (
                        <LawyerSilhouette variant={idx >= 0 ? idx : 0} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="overline text-[10px] text-[#4A6A8F] truncate">
                        {lang === "tr" ? m.title_tr : m.title_en}
                      </div>
                      <div className="font-serif-ap text-xl text-[#0F2746] mt-1.5 leading-tight group-hover:text-[#C4A47C] transition-colors">
                        {m.name}
                      </div>
                      <div className="overline text-[10px] text-[#0F2746]/50 mt-3">
                        {lang === "tr" ? "Profil →" : "Profile →"}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
